/**
 * Lines the peon says to one person by name (docs/voice.md "Lines by name"). Each is a template
 * here; the server renders it in the peon's own ElevenLabs voice the first time a name asks for it
 * (server/voicelines.ts), keeps the clip in R2 for the household, and every app keeps its copy, so
 * a name is voiced once and played from then on. Owner, 2026-10-28: "We need to ensure we aren't
 * regenerating the lines each launch for this use case and wasting credits. Only a name change or
 * new login would cause a regen".
 *
 * Adding a line is one entry in PERSONAL_LINES and a caller that plays it. Changing a line's words,
 * or bumping its version, makes each name's clip again the next time that name asks for it. Lines
 * that are the same for everyone stay in scripts/make-peon-voice.py, rendered once and shipped.
 * scripts/draft-voicelines.mjs drafts new lines in the peon's voice with Claude, for a person to
 * pick from; nothing it writes reaches the app until someone adds it here or to that script.
 */
import { NAME_MAX } from "./peons.ts";

/**
 * The peon's voice and how a line is rendered: the voice, model, seed and settings of
 * scripts/make-peon-voice.py, so a line by name sounds like the lines in the app (docs/voice.md).
 * 24 kHz PCM is the best raw format the account's plan gives (44.1 kHz PCM needs Pro); the voice
 * has nothing above 8 kHz to lose, measured on its takes.
 */
export const PEON_VOICE = {
  voiceId: "i7n8HHj1MSBdo1eKYczF",
  modelId: "eleven_v3",
  seed: 42,
  stability: 1.0,
  similarityBoost: 0.8,
  outputFormat: "pcm_24000",
  sampleRate: 24000,
} as const;

export interface PersonalLine {
  /** What he says, with `{name}` where the person's name goes. */
  text: string;
  /** Bumped to make every name's clip again with the same words (a new voice setting, a better cut). */
  version: number;
}

export const PERSONAL_LINES = {
  /** The launch greeting (flag helloUI). Owner, 2026-10-28: playing "Hello [name]!" when the app launches. */
  hello: { text: "Hello, {name}!", version: 1 },
} as const satisfies Record<string, PersonalLine>;

export type PersonalLineKey = keyof typeof PERSONAL_LINES;

export function isPersonalLineKey(value: string): value is PersonalLineKey {
  return Object.prototype.hasOwnProperty.call(PERSONAL_LINES, value);
}

/**
 * The name as the peon says it: trimmed, whitespace collapsed, brackets and braces dropped (the
 * voice model reads `[sighs]` as a direction, and `{name}` is the template's own mark), control
 * characters gone, at most NAME_MAX characters. Null when nothing is left to say.
 */
export function spokenName(name: string | null | undefined): string | null {
  if (!name) return null;
  const said = name
    .replace(/[\u0000-\u001f\u007f[\]{}<>]/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, NAME_MAX)
    .trim();
  return said ? said : null;
}

/** What `key` says to `name` ("Hello, Hue!"), or null when the name has nothing to say. */
export function personalLineText(key: PersonalLineKey, name: string | null | undefined): string | null {
  const said = spokenName(name);
  return said ? PERSONAL_LINES[key].text.replace("{name}", said) : null;
}

/**
 * The voice channel's directory for lines by name: what the players pass as a line's `dir`, so the
 * speech bubble and the mouth find its words and envelope (the players' speechTextFor and
 * loadEnvelopes), and the route the clips are served from (functions/api/voice/[line].ts).
 */
export const PERSONAL_BASE = "/api/voice/";

/**
 * The clip's address for `name`: the name and the line's version are in it, so every cache in the
 * way (the browser's, the apps' file) holds one name's clip and a rename or a new version asks
 * afresh. The server answers only for the caller's own name.
 */
export function personalLineUrl(key: PersonalLineKey, name: string | null | undefined): string | null {
  const said = spokenName(name);
  if (!said) return null;
  return `${PERSONAL_BASE}${key}?name=${encodeURIComponent(said)}&v=${PERSONAL_LINES[key].version}`;
}

/**
 * How long the launch waits for the greeting by name before it says an ordinary line instead.
 * Only a name's first launch waits at all: the server is making the clip, about 1.6 s of
 * ElevenLabs on a warm server and more while the app itself is still loading (2.5 s was missed on
 * the first try, 2026-10-28). Later launches play the kept copy at once.
 */
export const HELLO_WAIT_MS = 4000;

/** Whether the launch greets by name: the flag, the original voice (the Warcraft set has no lines by name) and a name to say. */
export function launchSaysHello(helloOn: boolean, originalVoice: boolean, name: string | null | undefined): boolean {
  return helloOn && originalVoice && spokenName(name) !== null;
}

// ---------- Rendering (server/voicelines.ts) ----------

/**
 * Not kept: the model tends to clip or rush whatever comes last in a take, so every line is
 * followed by this and cut before it (docs/voice.md "Rendering the lines").
 */
export const THROWAWAY = "Okay. That is all.";
export const TAKE_SEPARATOR = "\n\n[pause] ";

/** The text sent for one line's take: the line, a pause, the throwaway. */
export function takeText(line: string): string {
  return `${line}${TAKE_SEPARATOR}${THROWAWAY}`;
}

/**
 * The ElevenLabs request for one line's take, without the key (the caller adds `xi-api-key`): the
 * server's (server/voicelines.ts) and scripts/draft-voicelines.mjs's `--hear`, so both render the
 * same way. The answer is JSON: `audio_base64` (24 kHz PCM) and `normalized_alignment`.
 */
export function takeRequest(line: string): { url: string; body: string } {
  return {
    url: `https://api.elevenlabs.io/v1/text-to-speech/${PEON_VOICE.voiceId}/with-timestamps?output_format=${PEON_VOICE.outputFormat}`,
    body: JSON.stringify({
      text: takeText(line),
      model_id: PEON_VOICE.modelId,
      seed: PEON_VOICE.seed,
      voice_settings: { stability: PEON_VOICE.stability, similarity_boost: PEON_VOICE.similarityBoost },
    }),
  };
}

/** Most new clips one household may have made in a day; past it the launch says an ordinary line. */
export const VOICE_DAILY_CAP = 20;
